// pms-core / domain — what dropping a Planning bar on a room means.
//
// A FUNCTIONAL CORE: it judges, never writes. The Planning asks it when the desk
// drops a stay on a unit, and shows what it says; `pms_assign_room_stay` (with
// its own checks) is what actually decides. Nothing here is an invariant — the
// database refuses a busy room whatever this says.
//
// Three outcomes, in this order:
//
//   REFUSED   a stay the PMS does not own (the Vega seed puts its room and dates
//             back within 30 minutes — docs/PMS-PROOF-BRIEF.md Q6), one that has
//             left, a room it is already in, another établissement's room, a
//             room already occupied during the stay, a room with no category.
//   WARNED    another category (surclassement · déclassement), and a drop on a
//             different day than the stay starts. Each needs a confirmation.
//   CLEAN     same category, same days: it just happens.
//
// A drop NEVER changes the stay's dates. The warning says so and says which
// dates stay; moving a stay in time reprices it and is a different act.

import { categoryDominates, soldCategory, type RoomFit } from './roomFit.js';
import type { PmsReservationStatus } from './pms.js';
import type { IsoDate, PropertyCode, RoomId, RoomTypeId } from './types.js';

/** What may be dragged: a stay still to arrive, or in the house. */
export const RACK_DRAGGABLE_STATUSES: readonly PmsReservationStatus[] = ['reserved', 'confirmed', 'checked_in'];

export interface DropStay {
  id: string;
  status: PmsReservationStatus;
  roomId: RoomId | null;
  roomTypeId: RoomTypeId;
  soldRoomTypeId?: RoomTypeId | null;
  checkinDate: IsoDate;
  checkoutDate: IsoDate; // exclusive
  propertyCode: PropertyCode;
  /** The booking came from Vega's seed (`pms_reservation.source_vega_resa`). */
  vegaOwned: boolean;
}

export interface DropTarget {
  roomId: RoomId;
  roomTypeId: RoomTypeId | null;
  propertyCode: PropertyCode;
}

/** Another live stay in the target room. */
export interface DropOccupant {
  id: string;
  roomId: RoomId | null;
  checkinDate: IsoDate;
  checkoutDate: IsoDate; // exclusive
}

export type DropRefusal =
  | { reason: 'vega' }
  | { reason: 'status' }
  | { reason: 'sameRoom' }
  | { reason: 'noCategory' }
  | { reason: 'otherProperty' }
  | { reason: 'busy'; checkinDate: IsoDate; checkoutDate: IsoDate };

export type DropWarning =
  /** The room is better than what was sold: a gift, always undoable. */
  | { kind: 'upgrade'; sold: RoomTypeId; room: RoomTypeId }
  /** The room is below what was sold, or not comparable: the rate question goes to the desk. */
  | { kind: 'below'; sold: RoomTypeId; room: RoomTypeId }
  /** The stay would start on another day than the one it is booked for. */
  | { kind: 'dates'; dropStart: IsoDate; checkinDate: IsoDate; checkoutDate: IsoDate };

export type DropVerdict =
  | ({ ok: false } & DropRefusal)
  | { ok: true; changesCategory: boolean; warnings: DropWarning[] };

export interface DropInput {
  stay: DropStay;
  target: DropTarget;
  /** The other live stays in the target room (the stay itself may be among them). */
  others: readonly DropOccupant[];
  /** The night the stay's FIRST night would land on, from where it was dropped
   *  and where it was grabbed. null: the drop gave no day (no date warning). */
  dropStart: IsoDate | null;
  fit: RoomFit;
}

export function judgeRackDrop({ stay, target, others, dropStart, fit }: DropInput): DropVerdict {
  if (stay.vegaOwned) return { ok: false, reason: 'vega' };
  if (!RACK_DRAGGABLE_STATUSES.includes(stay.status)) return { ok: false, reason: 'status' };
  if (stay.propertyCode !== target.propertyCode) return { ok: false, reason: 'otherProperty' };
  if (stay.roomId === target.roomId) return { ok: false, reason: 'sameRoom' };
  // « Unknown » is not a licence to place: a room nobody typed cannot be compared.
  if (target.roomTypeId === null) return { ok: false, reason: 'noCategory' };

  const clash = others.find(
    (o) => o.id !== stay.id && o.roomId === target.roomId && o.checkinDate < stay.checkoutDate && stay.checkinDate < o.checkoutDate,
  );
  if (clash !== undefined) return { ok: false, reason: 'busy', checkinDate: clash.checkinDate, checkoutDate: clash.checkoutDate };

  const warnings: DropWarning[] = [];
  const sold = soldCategory(stay);
  const room = target.roomTypeId;
  const changesCategory = room !== stay.roomTypeId;
  if (room !== sold) {
    warnings.push(categoryDominates(fit, room, sold) ? { kind: 'upgrade', sold, room } : { kind: 'below', sold, room });
  }
  if (dropStart !== null && dropStart !== stay.checkinDate) {
    warnings.push({ kind: 'dates', dropStart, checkinDate: stay.checkinDate, checkoutDate: stay.checkoutDate });
  }
  return { ok: true, changesCategory, warnings };
}
